// src/services/optimization-studio-service.ts
import { apiFetch } from "../../../api/api-fetch";
import type { ArtifactClaimCheck } from "@/app/components/fix-actions/artifact-claim-check";

export type ArtifactType =
  | "Voice & Messaging"
  | "Comparison Asset"
  | "Evidence & Citation Pack"
  | "Decision Framework"
  | "Structured Data / Schema"
  | "Data Artifact"
  | "FAQ / Knowledge Base"
  | "Scenario-Based Q&A"
  | "Boundary / Trust Artifact"
  | string;

export type ArtifactStatus =
  | "Draft"
  | "In Review"
  | "Ready"
  | "Ready to Launch"
  | "Deployed"
  | string;

export interface StrategyArtifact {
  id: string;
  name: string;
  type: ArtifactType;

  supportingActionId: number;
  supportingActionTitle: string;
  strategicTheme: string;

  status: ArtifactStatus;
  /** No longer set (always 0): it was the model grading itself. */
  confidence: number;
  /** #132: the claim check of the asset's text against the brand record. */
  claimCheck?: ArtifactClaimCheck;

  deploymentTargets: string[];
  generatedAt: string;
  lastUpdated: string;

  intent: string;
  aiReasoningBehavior: string;
  relationshipToAction: string;

  /**
   * structurePreview — lightweight summary shown in list rows.
   * Backend may return either { type, content } or a flat object.
   * We normalise to { type, content } here.
   */
  structurePreview: { type: string; content: any };

  /**
   * fullContent — the full generated payload rendered by FullContentRenderer.
   * Backend always injects a `type` key; we guarantee it here too.
   */
  fullContent: Record<string, any>;

  generatedUsing?: string;
  deployedDate?: string;
  deploymentUrls?: Array<{ target: string; url: string }>;

  /**
   * Slot metadata — stored in backend artifact_json at generation time.
   *
   * slotIndex: which artifact slot on the parent action (0-based) this
   *   generated asset corresponds to. Stored via enrichedConfig.slotIndex
   *   in handleGenerate() → GenerateAssetRequest.config.slotIndex →
   *   artifact_json.slotIndex in the backend.
   *
   * slotTitle: human-readable title of the slot at generation time.
   *   Used as a fallback for slot resolution when slotIndex is unavailable
   *   (e.g. assets generated before this feature was added).
   *
   * These two fields are read by artifactTracker.hydrateFromArtifacts()
   * to reconstruct the full per-slot tracker state from the backend on
   * every page load, replacing the old sessionStorage approach.
   */
  slotIndex?: number;
  slotTitle?: string;
}

export interface OptimizationStudioData {
  artifacts: StrategyArtifact[];
  groupedByAction: Map<number, StrategyArtifact[]>;
}

type AssetsListResponse = { items: any[] };

function withQuery(path: string, query: Record<string, string | undefined>) {
  const usp = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== "")
      usp.set(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `${path}${path.includes("?") ? "&" : "?"}${qs}` : path;
}

function normaliseStructurePreview(raw: any): { type: string; content: any } {
  if (raw && typeof raw === "object") {
    if ("type" in raw) return raw as { type: string; content: any };
    // flat object — wrap it
    return { type: "generic", content: raw };
  }
  return { type: "list", content: [] };
}

function normaliseFullContent(raw: any): Record<string, any> {
  if (raw && typeof raw === "object") {
    // Unwrap { type, data } envelope if data is also an object
    if ("data" in raw && typeof raw.data === "object") {
      return { ...raw.data, type: raw.type ?? raw.data?.type ?? "generic" };
    }
    // Already flat with a type key — return as-is
    if ("type" in raw) return raw;
    // Flat without type — add generic
    return { ...raw, type: "generic" };
  }
  return { type: "generic" };
}

export function toStrategyArtifact(raw: any): StrategyArtifact | null {
  if (!raw) return null;

  const id = String(raw.id ?? "");
  const name = String(raw.name ?? raw.title ?? "");
  const type = String(raw.type ?? "");
  const supportingActionId = Number(
    raw.supportingActionId ?? raw.supporting_action_id ?? raw.actionId ?? 0
  );

  if (!id || !name || !type || !Number.isFinite(supportingActionId)) return null;

  return {
    id,
    name,
    type,
    supportingActionId,
    supportingActionTitle: String(
      raw.supportingActionTitle ?? raw.supporting_action_title ?? ""
    ),
    strategicTheme: String(raw.strategicTheme ?? raw.strategic_theme ?? ""),
    status: String(raw.status ?? "Draft"),
    confidence: Number(raw.confidence ?? 0),

    deploymentTargets: Array.isArray(raw.deploymentTargets)
      ? raw.deploymentTargets.map(String)
      : [],
    generatedAt: String(raw.generatedAt ?? raw.createdAt ?? ""),
    lastUpdated: String(raw.lastUpdated ?? raw.updatedAt ?? ""),

    intent: String(raw.intent ?? ""),
    aiReasoningBehavior: String(raw.aiReasoningBehavior ?? ""),
    relationshipToAction: String(raw.relationshipToAction ?? ""),

    structurePreview: normaliseStructurePreview(raw.structurePreview),
    fullContent: normaliseFullContent(raw.fullContent),

    generatedUsing: raw.generatedUsing ? String(raw.generatedUsing) : undefined,
    deployedDate: raw.deployedDate ? String(raw.deployedDate) : undefined,
    deploymentUrls: Array.isArray(raw.deploymentUrls)
      ? raw.deploymentUrls.map((x: any) => ({
          target: String(x.target ?? ""),
          url: String(x.url ?? ""),
        }))
      : undefined,

    // ── Slot metadata (persisted in backend, used for tracker hydration) ──
    // slotIndex is stored as a number in artifact_json; default to 0 for
    // assets generated before this feature was introduced.
    slotIndex:
      typeof raw.slotIndex === "number"
        ? raw.slotIndex
        : typeof raw.slotIndex === "string" && raw.slotIndex !== ""
        ? Number(raw.slotIndex)
        : undefined,
    slotTitle:
      typeof raw.slotTitle === "string" && raw.slotTitle.trim() !== ""
        ? raw.slotTitle
        : undefined,
  };
}

export async function fetchOptimizationStudioData(args: {
  brandId: string;
}): Promise<OptimizationStudioData> {
  const url = withQuery("/fix-actions/assets/list", { brandId: args.brandId });
  const res = await apiFetch<AssetsListResponse>(url, { method: "GET" });

  const artifacts: StrategyArtifact[] = (res?.items || [])
    .map(toStrategyArtifact)
    .filter(Boolean) as StrategyArtifact[];

  const groupedByAction = new Map<number, StrategyArtifact[]>();
  for (const a of artifacts) {
    if (!groupedByAction.has(a.supportingActionId))
      groupedByAction.set(a.supportingActionId, []);
    groupedByAction.get(a.supportingActionId)!.push(a);
  }

  return { artifacts, groupedByAction };
}